import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert } from 'antd';
import { PdfPreviewPane } from '@/features/report-detail';
import type { Template } from '@/models/template';
import { useReportStudies } from './hooks/useReportStudies';
import { useTemplatePreview } from './hooks/useTemplatePreview';
import { PreviewStudyPicker } from './PreviewStudyPicker';
import styles from './TemplateEditor.module.css';

interface TemplatePreviewPanelProps {
  template: Template;
}

/** The PDF of the unsaved template, rendered against a study with a report. */
export function TemplatePreviewPanel({ template }: TemplatePreviewPanelProps) {
  // The chosen study lives in the URL so a reload keeps it. Until one is
  // picked, preview against the most recent study that has a report.
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: recentStudies } = useReportStudies();
  const studyUuid =
    searchParams.get('study') ?? recentStudies?.results[0]?.uuid;
  const handleStudyChange = useCallback(
    (next: string) => setSearchParams({ study: next }, { replace: true }),
    [setSearchParams]
  );

  const preview = useTemplatePreview(template, studyUuid);

  return (
    <div className={styles.previewColumn}>
      <div className={styles.previewToolbar}>
        <PreviewStudyPicker value={studyUuid} onChange={handleStudyChange} />
      </div>
      <div className={styles.previewBody}>
        {studyUuid ? (
          <PdfPreviewPane
            pdfBlob={preview.pdfBlob}
            isLoading={preview.isLoading}
            isFetching={preview.isFetching}
            error={preview.error}
          />
        ) : (
          <Alert
            className={styles.previewEmpty}
            type="info"
            title="Pick a study with a report to preview the PDF"
            showIcon
          />
        )}
      </div>
    </div>
  );
}
